// Factual portfolio content for Jeremiah Magdael.
// Sources: Jeremiah_Magdael_Resume_2026.pdf and prior portfolio content.
// Do not invent employers, dates, certifications, metrics, or outcomes.

export const personalInfo = {
  name: "Jeremiah Magdael",
  role: "Website Security Analyst",
  positioning: "Full-Stack Engineering · Java · AWS & AI",
  statement: "I secure, build, and understand web systems.",
  support:
    "AWS-certified Computer Engineering professional working across website security, web infrastructure, full-stack development, Java, automation, and applied AI.",
  email: "jeremiahmagdael@gmail.com",
  location: "Manila, Philippines",
  website: "https://jeremiahmagdael.com",
  github: "https://github.com/Queas",
  linkedin: "https://www.linkedin.com/in/jeremiah-magdael-361278205",
  resumeUrl: `${process.env.PUBLIC_URL || "."}/Jeremiah_Magdael_Resume_2026.pdf`,
};

const asset = (name) => `${process.env.PUBLIC_URL || "."}/${name}`;

export const about = {
  paragraphs: [
    "I work across website security, web infrastructure, and full-stack software development. My background includes WordPress, cPanel/WHM/Plesk, VPS and shared hosting, DNS, SSL/TLS, Java and Spring Boot, React, PHP, Python/FastAPI, REST APIs, and SQL databases.",
    "Working across development and security has shaped how I engineer software: understand the complete system, reduce unnecessary complexity, verify assumptions, and build things that remain understandable after they ship.",
    "I'm extending that foundation into cloud and applied AI. I hold AWS Certified Cloud Practitioner and AWS Certified AI Practitioner credentials and use AI/LLM tooling for automation, developer workflows, analysis, and product capabilities without treating AI as a substitute for sound engineering.",
  ],
  afterEditor: [
    "DNS",
    "SSL/TLS",
    "Servers",
    "Deployments",
    "Databases",
    "HTTP",
    "Production failures",
    "Security problems",
    "Users",
  ],
};

export const capabilities = [
  {
    id: "application",
    label: "Application",
    items: [
      "Java",
      "Spring Boot",
      "React",
      "JavaScript",
      "TypeScript",
      "PHP",
      "Python",
      "FastAPI",
      "Node.js",
    ],
  },
  {
    id: "data",
    label: "Data & APIs",
    items: ["REST APIs", "PostgreSQL", "MySQL", "MongoDB", "Firebase", "ETL"],
  },
  {
    id: "security",
    label: "Security & Web Infrastructure",
    items: [
      "Website Security",
      "SSL/TLS",
      "DNS",
      "Domains",
      "WordPress",
      "cPanel / WHM / Plesk",
      "VPS / Shared Hosting",
      "Web Hosting",
      "Linux",
      "HTTP",
      "Server Troubleshooting",
    ],
  },
  {
    id: "engineering",
    label: "Engineering",
    items: [
      "Git",
      "GitHub",
      "Testing",
      "Debugging",
      "System Architecture",
      "Automation",
      "Deployment",
    ],
  },
  {
    id: "ai",
    label: "Applied AI",
    items: [
      "LLM Integration",
      "AI-assisted Development",
      "Automation",
      "Agent Workflows",
      "Prompt Engineering",
      "RAG Concepts",
    ],
  },
  {
    id: "operations",
    label: "Support & Operations",
    items: [
      "Jira",
      "Ticketing / CRM Workflows",
      "Microsoft Teams",
      "Slack",
      "Technical Documentation",
      "Issue Prioritization",
      "Escalation & Resolution",
    ],
  },
];

export const principles = [
  {
    title: "Understand the system",
    body: "Look beyond a single framework or application layer.",
  },
  {
    title: "Secure by default",
    body: "Security is part of implementation, not an afterthought.",
  },
  {
    title: "Verify the output",
    body: "Prefer observable evidence over assumptions.",
  },
  {
    title: "Build for maintainability",
    body: "Readable systems beat clever systems.",
  },
  {
    title: "Automate responsibly",
    body: "Automation should reduce repetitive work without hiding important decisions.",
  },
  {
    title: "Ship useful things",
    body: "Technology exists to solve actual problems.",
  },
];

export const appliedAi = {
  headline: "AI is another system component—not a replacement for engineering.",
  body: "Applied AI is an active area of practice. I integrate LLM capabilities into existing engineering workflows and products with the same standards I apply to any other subsystem: clear boundaries, observable behavior, and human oversight where decisions matter.",
  topics: [
    "LLM integration",
    "AI-assisted development",
    "Intelligent automation",
    "Structured agent workflows",
    "Tool orchestration",
    "Evaluation",
    "Human-in-the-loop workflows",
    "AI features inside existing products",
  ],
  note: "AWS AI certified — applied practice built on full-stack, security, and infrastructure experience.",
};

export const certifications = [
  {
    name: "AWS Certified Cloud Practitioner",
    issuer: "Amazon Web Services",
    detail: "Cloud fundamentals, core AWS services, security, architecture, and cost concepts.",
  },
  {
    name: "AWS Certified AI Practitioner",
    issuer: "Amazon Web Services",
    detail: "AI/ML and generative AI fundamentals, responsible AI, and AWS AI services.",
  },
  {
    name: "Fundamentals of Artificial Intelligence",
    issuer: "Professional development",
    detail: "Foundational AI, machine learning, generative AI, and responsible-AI concepts.",
  },
  {
    name: "Certificate of Professional Mastery",
    issuer: "Egotecworld",
    period: "Oct 2026",
    detail: "100-module curriculum including Full-Stack Development and Remote Engineering.",
    credentialId: "ETW-853F4C3FC9",
  },
];

export const education = {
  credential: "Diploma — Computer Engineering Technologies / Technicians",
  school: "Polytechnic University of the Philippines",
  location: "Manila, Philippines",
  period: "Jun 2016 – May 2019",
};

export const experience = [
  {
    id: "godaddy",
    company: "GoDaddy",
    location: "Philippines",
    roles: [
      { title: "Website Security Analyst", period: "Feb 2024 – Oct 2026" },
      { title: "Web Hosting Consultant", period: "Oct 2023 – Oct 2026" },
    ],
    summary:
      "Website security and web hosting work across real production websites and infrastructure.",
    highlights: [
      "Investigate website-security concerns and technical issues affecting websites and web infrastructure.",
      "Troubleshoot WordPress, SSL/TLS certificates, DNS, domains, cPanel/WHM/Plesk, shared/VPS hosting, email, databases, and server/application behavior.",
      "Apply full-stack development knowledge to trace issues across frontend, backend, infrastructure, and deployment layers while supporting secure remediation.",
    ],
  },
  {
    id: "conduent",
    company: "Conduent",
    location: "",
    roles: [
      {
        title: "Customer Experience Specialist III",
        period: "Jan 2023 – Aug 2023",
      },
    ],
    summary: "Structured customer-facing technical issue resolution.",
    highlights: [
      "Handled customer-facing issue resolution in a structured service environment, strengthening technical communication and problem diagnosis.",
    ],
  },
  {
    id: "sevenseven",
    company: "Seven Seven",
    location: "National Capital Region, Philippines",
    roles: [
      { title: "Full-Stack Developer Training", period: "Mar 2022 – Jul 2022 (5 mos)" },
      { title: "ETL Engineer", period: "Jul 2022 (1 mo)" },
    ],
    summary:
      "Java full-stack application development followed by ETL / data-integration work.",
    highlights: [
      "Completed company-provided Java full-stack training covering Java/Spring Boot, React/JavaScript, REST APIs, relational databases, and full-stack application workflows; no formal certification was issued.",
      "Supported ETL and data-integration work after the full-stack training assignment.",
    ],
  },
  {
    id: "alorica",
    company: "Alorica",
    location: "National Capital Region, Philippines",
    roles: [
      { title: "Advanced Technical Support", period: "Oct 2021 – Jan 2022" },
    ],
    summary: "Advanced technical troubleshooting for production service issues.",
    highlights: [
      "Provided advanced technical troubleshooting and customer support for production service issues.",
    ],
  },
];

export const hubLikha = {
  company: "HUB Likha",
  role: "Lead Developer",
  period: "Ongoing",
  summary:
    "Lead development of public-facing web products across full-stack engineering, architecture, automation, security, and delivery.",
};

export const projects = {
  featured: [
    {
      id: "tanaw",
      title: "Tanaw",
      subtitle: "Website Intelligence",
      category: "Product Engineering · HUB Likha",
      purpose:
        "A website intelligence product that gathers and correlates technical signals across the modern web.",
      context:
        "Active public product focused on making website technology, infrastructure, security, performance, privacy, and related signals easier to inspect in one place.",
      status: "Active product",
      statusTone: "active",
      href: "https://tanaw.hublikha.com",
      hrefLabel: "Visit Tanaw",
      technologies: [
        "Browser tooling",
        "Web product",
        "APIs",
        "Security & infrastructure analysis",
        "Frontend UX",
      ],
      detailLabel: "Technology",
      secondaryDetailLabel: "Signals explored",
      secondaryDetails: [
        "Technologies",
        "Infrastructure",
        "Security",
        "SSL/TLS",
        "DNS",
        "Registrar",
        "ASN / network",
        "Performance",
        "SEO",
        "Accessibility",
        "Privacy",
        "Network behavior",
        "Certificate intelligence",
      ],
      image: asset("tanaw.png"),
      imageAlt: "Tanaw website intelligence product interface",
      secondaryImage: asset("tanaw-mock.png"),
      secondaryImageAlt: "Tanaw supporting product interface",
      caseStudy: {
        problem:
          "Website information is fragmented across many tools. Technology, infrastructure, security, certificates, DNS, performance, SEO, accessibility, privacy, and network behavior rarely sit in one coherent view.",
        approach:
          "Bring useful technical signals from multiple layers of the web into one clear website-intelligence experience without flattening the underlying evidence.",
        role:
          "Product thinking, engineering, browser tooling, frontend UX, and security/infrastructure analysis.",
        outcome:
          "An active website-intelligence product that demonstrates cross-layer web engineering from application surfaces down through certificates, DNS, and network behavior.",
      },
    },
    {
      id: "hanapserbisyo",
      title: "HanapSerbisyo",
      subtitle: "Service-work trust platform",
      category: "Product Engineering · HUB Likha",
      purpose:
        "An in-development local-services platform designed to help clients and workers find each other through skills and location while preserving clearer service agreements and work evidence.",
      context:
        "Current website and Android pilot surfaces are implemented. The product is still under development and is being prepared for a controlled pilot; no public-launch claim is made here.",
      status: "In development",
      statusTone: "development",
      technologies: [
        "React",
        "TypeScript",
        "Express",
        "SQLite",
        "Tailwind CSS",
        "Capacitor Android",
      ],
      detailLabel: "Technology",
      secondaryDetailLabel: "Current product surfaces",
      secondaryDetails: [
        "Marketplace discovery",
        "Skills + location matching",
        "Transparent scope and quote history",
        "Evidence workflow",
        "Client confirmation",
        "Private worker service history",
        "Website + Android pilot",
      ],
      image: asset("hanapserbisyo-desktop.png"),
      imageAlt: "HanapSerbisyo current desktop interface from the development workspace",
      secondaryImage: asset("hanapserbisyo-mobile.png"),
      secondaryImageAlt: "HanapSerbisyo current mobile interface from the development workspace",
      caseStudy: {
        problem:
          "Local service work can be difficult to discover and verify. Information is scattered, expectations can become unclear, and workers often lack a durable record of completed service.",
        approach:
          "Combine marketplace discovery with clearer service scope, quote history, evidence, client confirmation, and a worker-facing service record instead of treating discovery and trust as separate problems.",
        role:
          "Lead development across product design, full-stack implementation, security boundaries, testing, and pilot-readiness work.",
        stateLabel: "Current state",
        outcome:
          "In development. Website and Android pilot surfaces exist and the product is being prepared for a controlled pilot. Hosting and real-device acceptance remain release work.",
      },
    },
  ],
  others: [
    {
      id: "snipeek",
      title: "Snipeek",
      category: "Security / OSINT Tool",
      purpose:
        "A web-based reconnaissance and OSINT project for collecting and organizing publicly available information about domains, IP addresses, and related internet infrastructure.",
      technologies: ["React", "Python", "FastAPI", "OSINT"],
      status: "Demo available",
      statusTone: "demo",
      image: asset("snipeek.png"),
      imageAlt: "Snipeek OSINT tool interface",
    },
    {
      id: "scanner",
      title: "Scanner",
      category: "Web Security Tool",
      purpose:
        "A web security assessment project for inspecting common security signals, configuration issues, exposed headers, and indicators useful during initial analysis.",
      technologies: ["React", "Python", "FastAPI", "Web Security"],
      status: "Demo available",
      statusTone: "demo",
      image: asset("scanner.png"),
      imageAlt: "Scanner web security tool interface",
    },
    {
      id: "deployhub",
      title: "Deploy Hub",
      category: "Website Delivery / Hosting",
      purpose:
        "A website delivery and managed-hosting concept focused on helping businesses launch and maintain an online presence.",
      technologies: ["React", "Node.js", "Tailwind CSS", "SSL"],
      status: "Case study available",
      statusTone: "demo",
      image: asset("deployhubph.png"),
      imageAlt: "Deploy Hub website delivery platform",
    },
  ],
};

export const navLinks = [
  { name: "Work", href: "#work" },
  { name: "Profile", href: "#profile" },
  { name: "Capabilities", href: "#capabilities" },
  { name: "Experience", href: "#experience" },
  { name: "AI", href: "#applied-ai" },
  { name: "Contact", href: "#contact" },
];

export function demoMailto(projectTitle) {
  const subject = encodeURIComponent(`Project Demo Request — ${projectTitle}`);
  const body = encodeURIComponent(
    `Hi Jeremiah,\n\nI'd like to request a demo / walkthrough of ${projectTitle}.\n\nThanks,`,
  );
  return `mailto:${personalInfo.email}?subject=${subject}&body=${body}`;
}
